import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner"
import { cn } from "../../lib/utils"
import { Button } from "../Button/Button"
import { IconButton } from "../IconButton/IconButton"

const toastStyles = cva(
  "w-full max-w-[28.1875rem] flex items-center gap-2 p-4 rounded-8 shadow-[0px_4px_16px_0px_rgba(28,33,40,0.25)]",
  {
    variants: {
      variant: {
        primary: "bg-feedback-primary-background text-root-inverse-text-base",
        alert: "bg-feedback-danger-background text-root-text-color-base",
        warning: "bg-feedback-warning-background text-root-text-color-base",
        success: "bg-feedback-success-background text-root-text-color-base",
        info: "bg-feedback-info-background text-root-text-color-base",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
)

interface ToastProps
  extends React.HTMLAttributes<HTMLOutputElement>,
    VariantProps<typeof toastStyles> {
  /** Toast message content */
  children: React.ReactNode
  /** Optional action button label */
  actionLabel?: string
  /** Optional action button handler */
  onAction?: () => void
  /** Optional close button handler */
  onClose?: () => void
  /** Show close button */
  showClose?: boolean
}

const ToastCard = ({
  className,
  children,
  actionLabel,
  onAction,
  onClose,
  showClose = true,
  variant = "primary",
  ...props
}: ToastProps) => {
  return (
    <output aria-live="polite" className={cn(toastStyles({ variant }), className)} {...props}>
      <div className="flex items-center gap-4 flex-1">
        <div className="flex-1 text-14 leading-3 font-400">{children}</div>
        {actionLabel && onAction && (
          <Button
            variant={variant === "primary" ? "tertiary" : "primary"}
            size="md"
            onClick={onAction}
            aria-label={actionLabel}
          >
            {actionLabel}
          </Button>
        )}
      </div>

      {showClose && (
        <IconButton
          variant={variant === "primary" ? "ghost-inverse" : "ghost"}
          size="md"
          icon="Close"
          onClick={onClose || (() => {})}
          aria-label="Close"
          className="p-2"
        />
      )}
    </output>
  )
}

ToastCard.displayName = "ToastCard"

function Toaster(props: React.ComponentProps<typeof SonnerToaster>) {
  return (
    <SonnerToaster
      position="bottom-center"
      offset={16}
      expand={true}
      style={
        {
          "--width": "28.1875rem",
          fontFamily:
            'var(--font-founders-grotesk), "Founders Grotesk", ui-sans-serif, system-ui, sans-serif',
        } as React.CSSProperties
      } // Override Sonner's internal CSS variables to stop it from overriding these styles.
      {...props}
    />
  )
}

type ToastVariant = NonNullable<ToastProps["variant"]>
interface ToastOptions {
  variant?: ToastVariant
  actionLabel?: string
  onAction?: () => void
  onClose?: () => void
  showClose?: boolean
  duration?: number
}

function toast(
  message: React.ReactNode,
  {
    variant = "primary",
    actionLabel,
    onAction,
    onClose,
    showClose = true,
    duration = 4000, // Default 4 seconds like Sonner's default
  }: ToastOptions = {}
) {
  return sonnerToast.custom(
    (t) => (
      <ToastCard
        variant={variant}
        actionLabel={actionLabel}
        onAction={
          onAction
            ? () => {
                onAction()
                sonnerToast.dismiss(t)
              }
            : undefined
        }
        onClose={
          onClose
            ? () => {
                onClose()
                sonnerToast.dismiss(t)
              }
            : () => sonnerToast.dismiss(t)
        }
        showClose={showClose}
      >
        {message}
      </ToastCard>
    ),
    { unstyled: true, duration }
  )
}

export { toast, Toaster, ToastCard }
